<template>
  <div class="stock-watch">
    <header id="blog-header">
      <div class="inner">
        <h1 class="page-title">股票监控</h1>
        <span class="page-desc">自选股票行情与估值(美股 · A股 · 港股)</span>
      </div>
    </header>

    <div class="stock-content">
      <div class="stock-toolbar">
        <button @click="loadStocks" :disabled="loading">{{ loading ? '刷新中...' : '刷新' }}</button>
        <a class="manage-link" href="/admin/stock_record/optionticker/">后台管理监控清单</a>
        <span class="model-name" v-if="modelName">评估模型: {{ modelName }}</span>
      </div>

      <div v-if="loading" class="loading">加载中...</div>
      <div v-if="error" class="error-msg">{{ error }}</div>
      <div v-if="!loading && stocks.length === 0" class="empty-hint">
        暂无自选股票。请在 <a href="/admin/stock_record/optionticker/">后台监控清单</a> 勾选股票后，
        执行每日分析任务或等待定时任务同步。
      </div>

      <template v-if="stocks.length > 0">
        <div class="stats-row">
          <div class="stat-item">
            <span>全部</span>
            <strong>{{ stocks.length }}</strong>
          </div>
          <div class="stat-item">
            <span>当前筛选</span>
            <strong>{{ filteredStocks.length }}</strong>
          </div>
          <div class="stat-item">
            <span>美股</span>
            <strong>{{ marketCount('us') }}</strong>
          </div>
          <div class="stat-item">
            <span>A股</span>
            <strong>{{ marketCount('ashare') }}</strong>
          </div>
          <div class="stat-item">
            <span>港股</span>
            <strong>{{ marketCount('hk') }}</strong>
          </div>
        </div>

        <div class="filter-panel">
          <label class="filter-field search-field">
            <span>搜索</span>
            <input v-model.trim="searchKeyword" type="search" placeholder="代码或名称">
          </label>
          <label class="filter-field">
            <span>市场</span>
            <select v-model="marketFilter">
              <option value="">全部市场</option>
              <option value="us">美股</option>
              <option value="ashare">A股</option>
              <option value="hk">港股</option>
            </select>
          </label>
          <label class="filter-field">
            <span>结论</span>
            <select v-model="verdictFilter">
              <option value="">全部结论</option>
              <option value="undervalued">低估</option>
              <option value="neutral">中性</option>
              <option value="overvalued">高估</option>
            </select>
          </label>
          <label class="filter-field">
            <span>估值日期</span>
            <select v-model="dateFilter">
              <option value="">全部日期</option>
              <option v-for="d in stockDates" :key="d" :value="d">{{ d }}</option>
            </select>
          </label>
          <label class="filter-field">
            <span>排序</span>
            <select v-model="sortKey">
              <option value="symbol">代码</option>
              <option value="name">名称</option>
              <option value="market">市场</option>
              <option value="change_pct">日涨跌</option>
              <option value="percentile_52w">52周分位</option>
              <option value="composite">综合分</option>
              <option value="date">估值日期</option>
            </select>
          </label>
          <button class="sort-dir" @click="sortDirection = sortDirection === 'asc' ? 'desc' : 'asc'">
            {{ sortDirection === 'asc' ? '升序' : '降序' }}
          </button>
          <button class="reset-btn" @click="resetFilters">重置</button>
        </div>

        <p class="model-hint">
          点击行查看完整模型评估过程。期权分析仅美股支持，A股/港股用于行情和估值监控。
        </p>

        <div v-if="filteredStocks.length === 0" class="empty-hint">
          当前筛选条件下没有匹配的股票。
        </div>

        <div class="table-wrapper" v-else>
          <table class="stock-table">
            <thead>
              <tr>
                <th><button class="sort-button" @click="sortBy('symbol')">代码 {{ sortIndicator('symbol') }}</button></th>
                <th><button class="sort-button" @click="sortBy('name')">名称 {{ sortIndicator('name') }}</button></th>
                <th><button class="sort-button" @click="sortBy('market')">市场 {{ sortIndicator('market') }}</button></th>
                <th>收盘</th>
                <th><button class="sort-button" @click="sortBy('change_pct')">日涨跌 {{ sortIndicator('change_pct') }}</button></th>
                <th>行情日期</th>
                <th><button class="sort-button" @click="sortBy('percentile_52w')">52周分位 {{ sortIndicator('percentile_52w') }}</button></th>
                <th><button class="sort-button" @click="sortBy('composite')">综合分 {{ sortIndicator('composite') }}</button></th>
                <th>结论</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="v in filteredStocks">
                <tr :key="v.symbol" class="stock-row" @click="toggleExpand(v.symbol)">
                  <td class="ticker-cell">{{ v.symbol }}</td>
                  <td>{{ v.name || '-' }}</td>
                  <td>{{ marketOf(v.symbol) }}</td>
                  <td>{{ formatPrice(v.close, 2) }}</td>
                  <td :class="changeClass(v.change_pct)">{{ formatSignedPercent(v.change_pct) }}</td>
                  <td>{{ v.quote_date || '-' }}</td>
                  <td>{{ formatPercent(v.percentile_52w, 0, false) }}</td>
                  <td :class="compositeClass(v)">{{ compositeOf(v) }}</td>
                  <td>
                    <span :class="'verdict-' + v.verdict">{{ verdictLabel(v.verdict) }}</span>
                    <span v-if="v.detail && v.detail.confidence && v.detail.confidence !== '无'" class="confidence">
                      置信{{ v.detail.confidence }}
                    </span>
                  </td>
                </tr>
                <tr v-if="expandedSymbol === v.symbol" :key="v.symbol + '-detail'" class="detail-row">
                  <td colspan="9">
                    <div class="eval-process">
                      <div class="detail-heading">
                        <h4>{{ v.name || v.symbol }} ({{ v.symbol }})</h4>
                        <span>估值日期 {{ v.date || '-' }}</span>
                      </div>
                      <div class="detail-grid">
                        <div><span>收盘</span><strong>{{ formatPrice(v.close, 2) }}</strong></div>
                        <div><span>前收</span><strong>{{ formatPrice(v.prev_close, 2) }}</strong></div>
                        <div><span>日涨跌</span><strong :class="changeClass(v.change_pct)">{{ formatSignedPercent(v.change_pct) }}</strong></div>
                        <div><span>52周分位</span><strong>{{ formatPercent(v.percentile_52w, 0, false) }}</strong></div>
                        <div><span>比值</span><strong>{{ formatPrice(v.ratio, 4) }}</strong></div>
                        <div><span>比值Z分</span><strong>{{ formatPrice(v.ratio_z_score, 2) }}</strong></div>
                        <div><span>1年Z分</span><strong>{{ formatPrice(v.z_score_1y, 2) }}</strong></div>
                        <div><span>2年Z分</span><strong>{{ formatPrice(v.z_score_2y, 2) }}</strong></div>
                        <div><span>5年Z分</span><strong>{{ formatPrice(v.z_score_5y, 2) }}</strong></div>
                      </div>
                      <table class="factor-table" v-if="v.detail && v.detail.factors">
                        <thead>
                          <tr><th>因子</th><th>原始值</th><th>得分</th><th>权重</th><th>贡献</th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="f in v.detail.factors" :key="f.key">
                            <td>{{ f.label }}</td>
                            <td>{{ f.value != null ? f.value : '缺失' }}</td>
                            <td>{{ f.score != null ? Number(f.score).toFixed(2) : '-' }}</td>
                            <td>{{ f.weight != null ? Number(f.weight).toFixed(2) : '-' }}</td>
                            <td>{{ f.score != null && f.weight != null ? (Number(f.score) * Number(f.weight)).toFixed(3) : '-' }}</td>
                          </tr>
                        </tbody>
                      </table>
                      <ol class="eval-steps" v-if="v.detail && v.detail.steps">
                        <li v-for="(s, si) in v.detail.steps" :key="si">{{ s }}</li>
                      </ol>
                      <p class="eval-warning" v-if="v.detail && v.detail.warning">提示：{{ v.detail.warning }}</p>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'stock-watch',
  data: function () {
    return {
      stocks: [],
      expandedSymbol: '',
      loading: false,
      error: '',
      searchKeyword: '',
      marketFilter: '',
      verdictFilter: '',
      dateFilter: '',
      sortKey: 'symbol',
      sortDirection: 'asc'
    }
  },
  computed: {
    modelName: function () {
      var first = this.stocks.find(function (v) { return v.detail && v.detail.model })
      return first ? first.detail.model : ''
    },

    stockDates: function () {
      var dates = {}
      this.stocks.forEach(function (v) {
        if (v.date) dates[v.date] = true
      })
      return Object.keys(dates).sort().reverse()
    },

    filteredStocks: function () {
      var self = this
      var keyword = (this.searchKeyword || '').toUpperCase()
      var rows = this.stocks.filter(function (v) {
        var symbol = (v.symbol || '').toUpperCase()
        var name = (v.name || '').toUpperCase()
        if (keyword && symbol.indexOf(keyword) === -1 && name.indexOf(keyword) === -1) return false
        if (self.marketFilter && self.marketKey(v.symbol) !== self.marketFilter) return false
        if (self.verdictFilter && v.verdict !== self.verdictFilter) return false
        if (self.dateFilter && v.date !== self.dateFilter) return false
        return true
      })

      rows.sort(function (a, b) {
        var av = self.sortValue(a, self.sortKey)
        var bv = self.sortValue(b, self.sortKey)
        var result = 0
        if (av == null && bv != null) result = 1
        else if (av != null && bv == null) result = -1
        else if (typeof av === 'number' && typeof bv === 'number') result = av - bv
        else result = String(av == null ? '' : av).localeCompare(String(bv == null ? '' : bv))
        return self.sortDirection === 'asc' ? result : -result
      })
      return rows
    }
  },
  mounted: function () {
    this.loadStocks()
  },
  methods: {
    loadStocks: function () {
      var self = this
      self.loading = true
      self.error = ''
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/valuations/?group=stock'
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.stocks = res.valuations || []
        } else {
          self.error = res.message || '加载失败'
        }
        self.loading = false
      }).catch(function (err) {
        self.error = '请求失败: ' + (err.statusText || err.status || 'unknown')
        self.loading = false
      })
    },

    resetFilters: function () {
      this.searchKeyword = ''
      this.marketFilter = ''
      this.verdictFilter = ''
      this.dateFilter = ''
      this.sortKey = 'symbol'
      this.sortDirection = 'asc'
    },

    sortBy: function (key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDirection = key === 'symbol' || key === 'name' || key === 'market' ? 'asc' : 'desc'
      }
    },

    sortIndicator: function (key) {
      if (this.sortKey !== key) return ''
      return this.sortDirection === 'asc' ? '↑' : '↓'
    },

    sortValue: function (row, key) {
      if (key === 'market') return this.marketOf(row.symbol)
      if (key === 'composite') return this.compositeNumber(row)
      if (key === 'date') return row.date
      return row[key]
    },

    marketKey: function (symbol) {
      if (/\.(SS|SZ|BJ)$/.test(symbol || '')) return 'ashare'
      if (/\.HK$/.test(symbol || '')) return 'hk'
      return 'us'
    },

    marketOf: function (symbol) {
      var key = this.marketKey(symbol)
      if (key === 'ashare') return 'A股'
      if (key === 'hk') return '港股'
      return '美股'
    },

    marketCount: function (key) {
      var self = this
      return this.stocks.filter(function (v) { return self.marketKey(v.symbol) === key }).length
    },

    changeClass: function (pct) {
      if (pct == null) return ''
      return Number(pct) > 0 ? 'chg-up' : (Number(pct) < 0 ? 'chg-down' : '')
    },

    toggleExpand: function (symbol) {
      this.expandedSymbol = this.expandedSymbol === symbol ? '' : symbol
    },

    compositeNumber: function (v) {
      if (!v.detail || v.detail.composite == null) return null
      var num = Number(v.detail.composite)
      return isNaN(num) ? null : num
    },

    compositeOf: function (v) {
      var c = this.compositeNumber(v)
      if (c == null) return '-'
      return (c > 0 ? '+' : '') + c.toFixed(2)
    },

    compositeClass: function (v) {
      var c = this.compositeNumber(v)
      if (c == null) return ''
      return Math.abs(c) >= (v.detail.threshold || 1.0) ? 'composite-active' : ''
    },

    verdictLabel: function (verdict) {
      return { undervalued: '低估', overvalued: '高估', neutral: '中性' }[verdict] || verdict || '-'
    },

    formatPrice: function (val, digits) {
      if (val == null || val === '') return '-'
      var num = Number(val)
      if (isNaN(num)) return '-'
      return num.toFixed(digits == null ? 2 : digits)
    },

    formatPercent: function (val, digits, scale) {
      if (val == null || val === '') return '-'
      var num = Number(val)
      if (isNaN(num)) return '-'
      if (scale !== false) num = num * 100
      return num.toFixed(digits == null ? 2 : digits) + '%'
    },

    formatSignedPercent: function (val) {
      if (val == null || val === '') return '-'
      var num = Number(val)
      if (isNaN(num)) return '-'
      return (num > 0 ? '+' : '') + num.toFixed(2) + '%'
    }
  }
}
</script>

<style scoped>
.stock-watch {
  min-height: 100vh;
}

#blog-header {
  padding: 3rem 1rem 2rem;
  text-align: center;
}

.page-title {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.page-desc {
  color: #888;
}

.stock-content {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 1rem 3rem;
}

.stock-toolbar {
  display: flex;
  gap: 14px;
  align-items: center;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.stock-toolbar button,
.sort-dir,
.reset-btn {
  border: 1px solid #d2d7df;
  background: #fff;
  color: #263238;
  border-radius: 4px;
  cursor: pointer;
  padding: 7px 14px;
}

.stock-toolbar button:disabled {
  cursor: wait;
  color: #999;
}

.manage-link {
  font-size: 14px;
  color: #3273dc;
  text-decoration: none;
}

.manage-link:hover {
  text-decoration: underline;
}

.model-name {
  font-size: 13px;
  color: #888;
  font-family: monospace;
  margin-left: auto;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(110px, 1fr));
  gap: 10px;
  margin: 10px 0 14px;
}

.stat-item {
  border: 1px solid #e6e9ef;
  border-radius: 6px;
  padding: 10px 12px;
  background: #fff;
}

.stat-item span {
  display: block;
  color: #777;
  font-size: 12px;
  margin-bottom: 4px;
}

.stat-item strong {
  color: #222;
  font-size: 20px;
}

.filter-panel {
  display: grid;
  grid-template-columns: minmax(180px, 1.4fr) repeat(4, minmax(120px, 1fr)) auto auto;
  gap: 10px;
  align-items: end;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e5e8ee;
  border-radius: 6px;
  background: #fafbfc;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-field span {
  color: #777;
  font-size: 12px;
}

.filter-field input,
.filter-field select {
  width: 100%;
  border: 1px solid #d2d7df;
  border-radius: 4px;
  padding: 7px 9px;
  background: #fff;
  font-size: 13px;
  box-sizing: border-box;
}

.model-hint {
  font-size: 12px;
  color: #888;
  margin: 6px 0 10px;
}

.loading,
.error-msg,
.empty-hint {
  padding: 1rem 0;
}

.error-msg {
  color: #c62828;
}

.empty-hint {
  color: #888;
  font-size: 14px;
}

.empty-hint a {
  color: #3273dc;
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid #edf0f4;
  border-radius: 6px;
}

.stock-table {
  width: 100%;
  min-width: 930px;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
}

.stock-table th,
.stock-table td {
  border-bottom: 1px solid #eee;
  padding: 8px 10px;
  text-align: left;
  vertical-align: middle;
}

.stock-table th {
  background: #f6f8fb;
  color: #555;
  font-weight: 600;
  white-space: nowrap;
}

.sort-button {
  border: none;
  background: transparent;
  padding: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
}

.ticker-cell {
  font-family: monospace;
  font-weight: bold;
}

.stock-row {
  cursor: pointer;
}

.stock-row:hover {
  background: #f5f8ff;
}

.chg-up {
  color: #c62828;
}

.chg-down {
  color: #2e7d32;
}

.composite-active {
  font-weight: bold;
}

.confidence {
  font-size: 11px;
  color: #999;
  margin-left: 4px;
}

.verdict-undervalued {
  color: #2e7d32;
  font-weight: bold;
}

.verdict-overvalued {
  color: #c62828;
  font-weight: bold;
}

.verdict-neutral {
  color: #777;
}

.detail-row td {
  background: #fbfcff;
  padding: 14px 16px;
}

.eval-process h4 {
  margin: 0;
  font-size: 15px;
}

.detail-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}

.detail-heading span {
  color: #888;
  font-size: 12px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.detail-grid div {
  border: 1px solid #e4e9f1;
  border-radius: 4px;
  padding: 8px 10px;
  background: #fff;
}

.detail-grid span {
  display: block;
  color: #777;
  font-size: 12px;
  margin-bottom: 3px;
}

.detail-grid strong {
  color: #222;
  font-size: 14px;
}

.factor-table {
  width: auto;
  min-width: 60%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 10px;
}

.factor-table th,
.factor-table td {
  border: 1px solid #e0e8f0;
  padding: 5px 10px;
  text-align: right;
}

.factor-table th:first-child,
.factor-table td:first-child {
  text-align: left;
}

.eval-steps {
  font-size: 12px;
  color: #555;
  line-height: 1.8;
  padding-left: 18px;
  margin: 0;
}

.eval-warning {
  color: #c62828;
  font-size: 12px;
  margin: 8px 0 0;
}

@media (max-width: 900px) {
  .stats-row {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }

  .filter-panel {
    grid-template-columns: 1fr 1fr;
  }

  .search-field {
    grid-column: 1 / -1;
  }

  .model-name {
    margin-left: 0;
    width: 100%;
  }
}

@media (max-width: 560px) {
  .filter-panel,
  .stats-row {
    grid-template-columns: 1fr;
  }

  .detail-heading {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
